import type { AnalysisSnapshot } from "../audio/applicationAudio"
import { canvasElement, htmlElement } from "./dom"

type SnapshotProvider = () => AnalysisSnapshot | undefined

class CanvasContextError extends Error {
  constructor(canvasId: string) {
    super(`Canvas 2D context unavailable: ${canvasId}`)
    this.name = "CanvasContextError"
  }
}

function canvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext("2d")
  if (context === null) throw new CanvasContextError(canvas.id)
  return context
}

function drawGrid(context: CanvasRenderingContext2D, width: number, height: number): void {
  context.fillStyle = "#05070a"
  context.fillRect(0, 0, width, height)
  context.strokeStyle = "#1d2633"
  context.lineWidth = 1
  for (let division = 1; division < 5; division += 1) {
    context.beginPath()
    context.moveTo(0, (height * division) / 5)
    context.lineTo(width, (height * division) / 5)
    context.stroke()
  }
}

function drawSpectrum(snapshot: AnalysisSnapshot | undefined): void {
  const canvas = canvasElement("spectrum-canvas")
  const context = canvasContext(canvas)
  drawGrid(context, canvas.width, canvas.height)
  if (snapshot === undefined) return
  context.beginPath()
  context.strokeStyle = "#58a6ff"
  context.lineWidth = 2
  let peakIndex = 0
  let peakValue = 0
  for (let index = 1; index < snapshot.frequency.length; index += 1) {
    const value = snapshot.frequency[index] ?? 0
    if (value > peakValue) {
      peakValue = value
      peakIndex = index
    }
    const normalized = index / snapshot.frequency.length
    const x = Math.log10(1 + normalized * 99) * (canvas.width / 2)
    const y = canvas.height - (value / 255) * canvas.height
    index === 1 ? context.moveTo(x, y) : context.lineTo(x, y)
  }
  context.stroke()
  const peakHertz = Math.round((peakIndex * snapshot.sampleRate) / (snapshot.frequency.length * 2))
  htmlElement("peak-readout").textContent = `Peak ${peakHertz} Hz`
}

function drawScope(snapshot: AnalysisSnapshot | undefined): void {
  const canvas = canvasElement("scope-canvas")
  const context = canvasContext(canvas)
  drawGrid(context, canvas.width, canvas.height)
  if (snapshot === undefined) return
  context.beginPath()
  context.strokeStyle = "#3fb950"
  context.lineWidth = 2
  let sumSquares = 0
  let peak = 0
  for (let index = 0; index < snapshot.waveform.length; index += 1) {
    const value = snapshot.waveform[index] ?? 0
    sumSquares += value * value
    peak = Math.max(peak, Math.abs(value))
    const x = (index / (snapshot.waveform.length - 1)) * canvas.width
    const y = canvas.height / 2 - value * (canvas.height * 0.44)
    index === 0 ? context.moveTo(x, y) : context.lineTo(x, y)
  }
  context.stroke()
  const rms = Math.sqrt(sumSquares / snapshot.waveform.length)
  const rmsDecibels = 20 * Math.log10(Math.max(rms, 0.00001))
  const peakDecibels = 20 * Math.log10(Math.max(peak, 0.00001))
  htmlElement("rms-readout").textContent = `RMS ${rmsDecibels.toFixed(1)} dBFS`
  htmlElement("peak-db-readout").textContent = `Peak ${peakDecibels.toFixed(1)} dBFS`
  htmlElement("level-value").textContent = rmsDecibels.toFixed(1)
  htmlElement("channels-value").textContent = String(snapshot.channelCount)
}

export function startVisualizers(provider: SnapshotProvider): () => void {
  let animationFrame = 0
  const render = (): void => {
    const snapshot = provider()
    drawSpectrum(snapshot)
    drawScope(snapshot)
    animationFrame = requestAnimationFrame(render)
  }
  render()
  return () => cancelAnimationFrame(animationFrame)
}
